<template>
  <v-container>
    <!-- Header band -->
    <div class="topbar">
      <div class="topbar__row">
        <v-btn
          icon
          variant="text"
          :title="$filters.capitalize($t('battery.back'))"
          :to="{ name: 'batteries' }"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="topbar__badge">
          <v-icon size="22">mdi-history</v-icon>
        </div>
        <div class="topbar__title min-width-0">
          <div class="topbar__name">
            {{ battery ? battery.name : $filters.capitalize($t("battery.history")) }}
          </div>
          <div class="topbar__sub">
            <template v-if="battery">{{ battery.tesla_id }} &middot; {{ battery.max_energy }} {{ $t("unity.kwh") }}</template>
            <template v-if="status"> &middot; {{ $filters.capitalize($t(`battery.${stateOf(status)}`)) }}</template>
            <template v-if="lastRefresh"> &middot; {{ $t("production.last_refresh") }} {{ $filters.formatTime(lastRefresh) }}</template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
            v-model="range"
            mandatory
            density="compact"
            variant="outlined"
            color="primary"
            class="range-toggle"
            @update:model-value="loadHistory"
          >
            <v-btn v-for="r in ranges" :key="r.value" :value="r.value" size="small">{{ r.label }}</v-btn>
          </v-btn-toggle>
          <v-btn
            v-if="battery_id"
            variant="tonal"
            size="small"
            prepend-icon="mdi-chart-line"
            :href="grafanaBatteryUrl(battery_id, range)"
            target="_blank"
            rel="noopener"
          >
            Grafana
          </v-btn>
          <v-btn
            icon
            variant="tonal"
            color="primary"
            size="small"
            :loading="loading"
            :title="$filters.capitalize($t('production.refresh'))"
            @click="load"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="topbar__progress"></v-progress-linear>
    </div>

    <template v-if="battery_id">
      <!-- KPI tiles from the latest status -->
      <div v-if="status" class="kpi-grid">
        <v-card flat class="kpi kpi--green">
          <div class="kpi__badge"><v-icon size="20">mdi-battery-high</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("battery.soc")) }}</div>
            <div class="kpi__value">
              <template v-if="socOf(status) !== null">{{ Math.round(socOf(status)) }} <span class="kpi__unit">%</span></template>
              <span v-else class="text-disabled">&ndash;</span>
            </div>
            <div class="kpi__hint">
              {{ $t("battery.energy_left") }} {{ $filters.formatFloat(status.energy_left / 1000) }} / {{ battery ? battery.max_energy : "–" }} {{ $t("unity.kwh") }}
            </div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--amber">
          <div class="kpi__badge"><v-icon size="20">mdi-flash</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("battery.power")) }}</div>
            <div class="kpi__value" :class="powerClass(status)">
              {{ signed(kw(status)) }}
              <span class="kpi__unit">{{ $t("unity.kw") }}</span>
            </div>
            <div class="kpi__hint">{{ $filters.capitalize($t(`battery.${stateOf(status)}`)) }}</div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--purple">
          <div class="kpi__badge"><v-icon size="20">mdi-sine-wave</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("battery.frequency")) }}</div>
            <div class="kpi__value">
              <template v-if="hasValue(status.frequency)">{{ Number(status.frequency).toFixed(2) }} <span class="kpi__unit">{{ $t("unity.hz") }}</span></template>
              <span v-else class="text-disabled">&ndash;</span>
            </div>
            <div class="kpi__hint">
              <template v-if="hasValue(status.grid)">{{ $t("battery.grid") }} {{ formatPower(status.grid) }}</template>
            </div>
          </div>
        </v-card>
        <v-card flat class="kpi kpi--blue">
          <div class="kpi__badge"><v-icon size="20">mdi-clock-outline</v-icon></div>
          <div class="min-width-0">
            <div class="kpi__label">{{ $filters.capitalize($t("production.last_data")) }}</div>
            <div class="kpi__value kpi__value--small">
              <span class="fresh-chip" :class="freshnessClass(status.time) || 'fresh'">{{ relativeTime(status.time) }}</span>
            </div>
            <div class="kpi__hint">{{ $filters.formatDateTime(status.time) }}</div>
          </div>
        </v-card>
      </div>

      <!-- Grafana panels -->
      <v-row dense class="mt-1">
        <v-col v-for="panel in panels" :key="panel.key" cols="12" lg="6">
          <v-card flat class="graf-card">
            <div class="graf-card__head">
              <v-icon size="small" class="mr-2" :color="panel.color">{{ panel.icon }}</v-icon>
              <span class="text-subtitle-2 font-weight-bold">{{ $filters.capitalize($t(panel.label)) }}</span>
            </div>
            <GrafanaFrame
              v-if="GRAFANA_EMBED_ENABLED"
              class="graf-frame"
              :src="grafanaBatteryPanelUrl(battery_id, panel.id, range)"
              :href="grafanaBatteryUrl(battery_id, range)"
              :title="$t(panel.label)"
              :hint="$t('machine.open_grafana')"
            ></GrafanaFrame>
            <a v-else :href="grafanaBatteryUrl(battery_id, range)" target="_blank" rel="noopener" class="graf-placeholder">
              <v-icon size="32" color="primary">{{ panel.icon }}</v-icon>
              <span>{{ $t("machine.open_grafana") }}</span>
              <span class="text-caption text-medium-emphasis">{{ $t("machine.embed_disabled") }}</span>
            </a>
          </v-card>
        </v-col>
      </v-row>

      <!-- Statuses table -->
      <v-card flat class="table-card">
        <div class="table-card__head">
          <v-icon size="small" class="mr-2" color="primary">mdi-table</v-icon>
          <span class="text-subtitle-1 font-weight-bold">{{ $filters.capitalize($t("battery.statuses", 2)) }}</span>
          <v-chip size="x-small" variant="tonal" class="ml-2">{{ history.length }}</v-chip>
          <span class="text-caption text-medium-emphasis ml-2">{{ rangeLabel }}</span>
          <span v-if="historyError" class="text-caption text-error ml-auto">{{ historyError }}</span>
        </div>
        <v-data-table
          class="history-table"
          :headers="headers"
          :items="history"
          :items-per-page="20"
          :loading="historyLoading"
          density="compact"
          :no-data-text="$filters.capitalize($t('battery.no_data'))"
        >
          <template v-slot:[`item.time`]="{ item }">
            <span class="text-no-wrap">{{ $filters.formatDateTime(item.time) }}</span>
          </template>
          <template v-slot:[`item.soe`]="{ item }">
            <div class="d-flex align-center">
              <v-progress-linear
                :model-value="socOf(item) || 0"
                :color="socColor(socOf(item))"
                height="6"
                rounded
                bg-color="grey-lighten-3"
                bg-opacity="1"
                class="mr-2"
                style="width: 70px"
              ></v-progress-linear>
              <span v-if="socOf(item) !== null">{{ Math.round(socOf(item)) }} %</span>
              <span v-else class="text-disabled">&ndash;</span>
            </div>
          </template>
          <template v-slot:[`item.battery_power`]="{ item }">
            <span :class="powerClass(item)">
              {{ signed(kw(item)) }}
              <span class="text-caption text-medium-emphasis">{{ $t("unity.kw") }}</span>
            </span>
          </template>
          <template v-slot:[`item.energy_left`]="{ item }">
            {{ $filters.formatFloat(item.energy_left / 1000) }}
            <span class="text-caption text-medium-emphasis">{{ $t("unity.kwh") }}</span>
          </template>
          <template v-slot:[`item.frequency`]="{ item }">
            <template v-if="hasValue(item.frequency)">
              {{ Number(item.frequency).toFixed(2) }}
              <span class="text-caption text-medium-emphasis">{{ $t("unity.hz") }}</span>
            </template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
          <template v-slot:[`item.grid`]="{ item }">
            <template v-if="hasValue(item.grid)">{{ formatPower(item.grid) }}</template>
            <span v-else class="text-disabled">&ndash;</span>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-card v-else flat class="empty-card text-medium-emphasis text-body-2">
      <v-icon size="36" class="mb-2" color="grey">mdi-battery-off-outline</v-icon>
      <div>{{ $filters.capitalize($t("battery.not_found")) }}</div>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import {
  isNone,
  grafanaBatteryUrl,
  grafanaBatteryPanelUrl,
  GRAFANA_BATTERY_PANELS,
  GRAFANA_EMBED_ENABLED
} from "@/functions.js";
import utils from "@/mixins/utils";
import GrafanaFrame from "@/components/GrafanaFrame.vue";

const RANGE_HOURS = { "now-6h": 6, "now-24h": 24, "now-3d": 72, "now-7d": 168 };

export default {
  components: { GrafanaFrame },
  mixins: [utils],

  computed: {
    battery_id() {
      const id = Number(this.$route.query.battery_id);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    ranges() {
      const h = this.$t("limitations.duration_units.hour");
      const d = this.$t("limitations.duration_units.day");
      return [
        { value: "now-6h", label: `6 ${h}` },
        { value: "now-24h", label: `24 ${h}` },
        { value: "now-3d", label: `3 ${d}` },
        { value: "now-7d", label: `7 ${d}` }
      ];
    },

    rangeLabel() {
      const r = this.ranges.find((x) => x.value === this.range);
      return r ? r.label : "";
    },

    panels() {
      return [
        { key: "soc", id: GRAFANA_BATTERY_PANELS.soc, label: "battery.soc_chart", icon: "mdi-battery-high", color: "success" },
        { key: "power", id: GRAFANA_BATTERY_PANELS.power, label: "battery.power_chart", icon: "mdi-flash", color: "warning" },
        { key: "frequency", id: GRAFANA_BATTERY_PANELS.frequency, label: "battery.frequency_chart", icon: "mdi-sine-wave", color: "purple" },
        { key: "energy", id: GRAFANA_BATTERY_PANELS.energy, label: "battery.energy_chart", icon: "mdi-battery-charging", color: "primary" }
      ];
    },

    headers() {
      const all = [
        { title: this.$filters.capitalize(this.$t("generic.date")), key: "time", sortable: true },
        { title: this.$filters.capitalize(this.$t("battery.soc")), key: "soe", sortable: true },
        { title: this.$filters.capitalize(this.$t("battery.power")), key: "battery_power", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("battery.energy_left")), key: "energy_left", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("battery.frequency")), key: "frequency", sortable: true, align: "end" },
        { title: this.$filters.capitalize(this.$t("battery.grid")), key: "grid", sortable: true, align: "end" }
      ];
      if (this.$vuetify.display.smAndDown) {
        return all.filter((h) => ["time", "soe", "battery_power"].includes(h.key));
      }
      return all;
    }
  },

  methods: {
    grafanaBatteryUrl,
    grafanaBatteryPanelUrl,

    hasValue(value) {
      return !isNone(value) && value !== "";
    },

    // Elum plant power stored in watts: kW below one megawatt, MW above
    formatPower(watts) {
      const w = Number(watts) || 0;
      if (Math.abs(w) >= 1000000) {
        return `${this.$filters.formatFloat(w / 1000000)} ${this.$t("unity.mw")}`;
      }
      return `${this.$filters.formatFloat(w / 1000)} ${this.$t("unity.kw")}`;
    },

    kw(item) {
      return (Number(item.battery_power) || 0) / 1000;
    },

    signed(value) {
      const txt = this.$filters.formatFloat(Math.abs(value));
      if (value > 0) return `+${txt}`;
      if (value < 0) return `-${txt}`;
      return txt;
    },

    // State of charge in %: the reported value, else energy left over the capacity
    socOf(item) {
      if (this.hasValue(item.soe)) {
        return Math.min(100, Math.max(0, Number(item.soe)));
      }
      const max = Number(this.battery ? this.battery.max_energy : 0);
      if (max > 0 && this.hasValue(item.energy_left)) {
        return Math.min(100, Math.max(0, (Number(item.energy_left) / 1000 / max) * 100));
      }
      return null;
    },

    // battery_power > 0: energy leaves the battery, < 0: it is being charged
    stateOf(item) {
      const p = this.kw(item);
      if (p > 0) return "discharging";
      if (p < 0) return "charging";
      return "idle";
    },

    powerClass(item) {
      const state = this.stateOf(item);
      if (state === "charging") return "text-primary";
      if (state === "discharging") return "text-success";
      return "";
    },

    socColor(soc) {
      if (soc === null) return "grey";
      if (soc < 20) return "error";
      if (soc < 50) return "warning";
      return "success";
    },

    relativeTime(value) {
      const minutes = Math.max(0, Math.round(moment().diff(moment(value), "minutes")));
      if (minutes < 60) return this.$t("production.minutes_ago", { n: minutes });
      const hours = Math.round(minutes / 60);
      if (hours < 48) return this.$t("production.hours_ago", { n: hours });
      return this.$t("production.days_ago", { n: Math.round(hours / 24) });
    },

    freshnessClass(last) {
      if (isNone(last)) return "over_5h";
      const age = moment().diff(moment(last), "hours", true);
      if (age >= 5) return "over_5h";
      if (age >= 3) return "over_3h";
      if (age >= 1) return "over_1h";
      return "";
    },

    // Latest status and battery description from the list endpoint (always available)
    loadStatus() {
      return this.$store
        .dispatch("battery_status/getCurrent")
        .then((items) => {
          const found = (Array.isArray(items) ? items : []).find(
            (bs) => Number(bs.battery_id || (bs.battery && bs.battery.pk)) === this.battery_id
          );
          this.status = found || null;
          if (found) {
            this.battery = found.battery;
          }
        })
        .catch((error) => {
          console.error("battery status:", error);
        });
    },

    // Statuses over the range for the table; a failure only empties the table
    loadHistory() {
      this.historyLoading = true;
      this.historyError = null;
      return this.$store
        .dispatch("battery_status/getHistory", { battery_id: this.battery_id, hours: RANGE_HOURS[this.range] || 24 })
        .then((items) => {
          this.history = items;
          if (items.length && items[0].battery && !this.battery) {
            this.battery = items[0].battery;
          }
        })
        .catch((error) => {
          console.error("battery history:", error);
          this.history = [];
          this.historyError = String(error && error.message ? error.message : error);
        })
        .finally(() => {
          this.historyLoading = false;
        });
    },

    load() {
      if (isNone(this.battery_id)) {
        this.status = null;
        this.history = [];
        return;
      }
      this.loading = true;
      Promise.allSettled([this.loadStatus(), this.loadHistory()]).then(() => {
        this.loading = false;
        this.lastRefresh = moment();
      });
    }
  },

  watch: {
    battery_id() {
      this.battery = null;
      this.load();
    }
  },

  mounted() {
    this.load();
  },

  data() {
    return {
      loading: false,
      historyLoading: false,
      historyError: null,
      lastRefresh: null,
      range: "now-24h",
      status: null,
      history: [],
      battery: null,
      GRAFANA_EMBED_ENABLED
    };
  }
};
</script>

<style scoped>
.over_1h {
  color: #c9a800;
}
.over_3h {
  color: #e07b00;
}
.over_5h {
  color: #d32f2f;
}
.fresh {
  color: #2e7d32;
}
.fresh-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  background: color-mix(in srgb, currentColor 12%, white);
}

/* Sticky header, pinned under the app bar while the page scrolls */
.topbar {
  position: sticky;
  top: var(--v-layout-top, 0px);
  z-index: 5;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #0093bf 0%, #1aa3a8 55%, #43a047 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px 10px 8px;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--on {
  background: #e3f4e4;
  color: #2e7d32;
}
.topbar__badge--off {
  background: #fde7e7;
  color: #d32f2f;
}
.topbar__title {
  flex: 1 1 200px;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__sub {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.range-toggle {
  border-color: rgba(0, 0, 0, 0.12);
}
.topbar__progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
.min-width-0 {
  min-width: 0;
}

/* KPI tiles */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}
.kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.kpi__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kpi--amber .kpi__badge {
  background: #fff3d6;
  color: #e08a00;
}
.kpi--blue .kpi__badge {
  background: #dff3f9;
  color: #0093bf;
}
.kpi--green .kpi__badge {
  background: #e3f4e4;
  color: #2e7d32;
}
.kpi--purple .kpi__badge {
  background: #ece6f7;
  color: #6a3fb5;
}
.kpi__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi__value {
  margin-top: 2px;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}
.kpi__value--small {
  font-size: 1rem;
}
.kpi__unit {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
}
.kpi__hint {
  margin-top: 2px;
  min-height: 1.1em;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Grafana panels */
.graf-card {
  padding: 12px 16px 10px;
  height: 100%;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.graf-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}
.graf-frame {
  display: block;
  width: 100%;
  height: 280px;
  border: 0;
  border-radius: 8px;
  background: #fafbfc;
}
.graf-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 140px;
  border-radius: 8px;
  border: 1px dashed rgba(0, 147, 191, 0.4);
  background: #f7fbfd;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  text-decoration: none;
}
.graf-placeholder:hover {
  background: #eef7fb;
}

/* Table */
.table-card {
  margin-top: 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}
.table-card__head {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafbfc;
}
.history-table :deep(th) {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.55) !important;
  background: #fff;
}
.history-table :deep(td) {
  font-variant-numeric: tabular-nums;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
}
.history-table :deep(tbody tr:hover td) {
  background-color: #f2f9fc;
}
.empty-card {
  margin-top: 10px;
  padding: 32px 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
}

@media (max-width: 900px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .topbar__row {
    flex-wrap: wrap;
  }
  .topbar__actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 700px) {
  .graf-frame {
    height: 220px !important;
  }
  .topbar__actions {
    flex-wrap: wrap;
    gap: 6px;
  }
  .range-toggle {
    width: 100%;
  }
  .range-toggle .v-btn {
    flex: 1;
  }
}
</style>
